import React from 'react'
import styled from 'styled-components'
import LoadingBar from './LoadingBar'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts'
import { format } from 'date-fns'

export interface DashboardAreaChartPoint {
  x: number
  y: number
}

interface DashboardAreaChartProps {
  data: DashboardAreaChartPoint[]
  height?: number
  isLoading?: boolean
  accentColor?: string
  showYAxis?: boolean
  formatXAxisLabelFn?: (value: number) => string
  formatXLabelFn?: (value: number) => string
  formatYLabelFn?: (value: number) => string
}

const ChartShell = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  margin-top: 4px;
  position: relative;
  width: 100%;
`

const ChartLoading = styled.div`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  position: absolute;
`

const DashboardAreaChart = ({
  data,
  height = 220,
  isLoading = false,
  accentColor = '#3464e6',
  showYAxis = false,
  formatXAxisLabelFn,
  formatXLabelFn,
  formatYLabelFn
}: DashboardAreaChartProps): React.ReactElement => {
  return <ChartShell $height={height}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          top: 8,
          right: showYAxis ? 12 : 6,
          bottom: 0,
          left: showYAxis ? 4 : 0
        }}>
        <>
          <CartesianGrid stroke="#edf1f7" vertical={false} />
          <XAxis
            dataKey="x"
            axisLine={false}
            tickLine={false}
            minTickGap={28}
            tick={{ fontSize: 11, fill: '#8b92a2' }}
            tickFormatter={(value) => formatXAxisLabelFn?.(Number(value)) ?? format(new Date(Number(value)), 'MMM d')}
          />
          <YAxis
            hide={!showYAxis}
            axisLine={false}
            tickLine={false}
            width={showYAxis ? 72 : 0}
            tick={{ fontSize: 11, fill: '#9aa2b2' }}
            tickFormatter={(value) => formatYLabelFn?.(Number(value)) ?? Number(value).toLocaleString()}
            domain={['auto', 'auto']}
          />
          <RechartsTooltip
            cursor={{ stroke: '#d5dae5', strokeDasharray: '4 4' }}
            contentStyle={{
              border: '1px solid #e3e7ef',
              borderRadius: 8,
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
              color: '#111827',
              fontSize: 13
            }}
            labelFormatter={(value) => formatXLabelFn?.(Number(value)) ?? format(new Date(Number(value)), 'MMM d, yyyy')}
            formatter={(value) => {
              const numericValue = Array.isArray(value) ? Number(value[0]) : Number(value)
              return [
                formatYLabelFn?.(numericValue) ?? numericValue.toLocaleString(),
                'Value'
              ]
            }}
          />
          <Area
            dataKey="y"
            type="monotone"
            stroke={accentColor}
            strokeWidth={2}
            fill={accentColor}
            fillOpacity={0.08}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: accentColor }}
          />
        </>
      </AreaChart>
    </ResponsiveContainer>
    {isLoading && <ChartLoading>
      <LoadingBar style={{ height: 12, width: 110 }} />
    </ChartLoading>}
  </ChartShell>
}

export default DashboardAreaChart
